import { useState, useEffect } from "react";
import { checkIpfsUrl } from "@/components/utils/checkUrl";
import { ItemFileData } from "@/types";
import {
  CollectionStandardData,
  CollectionMediaLoadingState,
} from "@/types/collection";

export function useCollectionMetadata(
  collectionUrl?: string,
  itemUrl?: string
) {
  const [metadata, setMetadata] = useState<CollectionStandardData | null>(null);
  const [itemMetadata, setItemMetadata] = useState<ItemFileData | null>(null);
  const [loading, setLoading] = useState<CollectionMediaLoadingState>({
    background: true,
    image: true,
    nftImage: true,
    loaded: false,
  });

  useEffect(() => {
    if (!collectionUrl || !itemUrl) return;

    const fetchWithRetry = async (
      url: string,
      maxRetries = 20,
      delay = 10000
    ) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            return response;
          }
          if (i === 0) {
            setLoading(prev => ({ ...prev, loaded: true }));
          }
        } catch (error) {
          console.error(
            `Network error on attempt ${i + 1}/${maxRetries}:`,
            error
          );
          if (i === 0) {
            setLoading(prev => ({ ...prev, loaded: true }));
          }
        }
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      throw new Error(`Failed to fetch after ${maxRetries} retries`);
    };

    const fetchData = async () => {
      try {
        const [collectionRes, itemRes] = await Promise.all([
          fetchWithRetry(collectionUrl),
          fetchWithRetry(itemUrl),
        ]);

        const [collectionData, itemData] = await Promise.all([
          collectionRes.json(),
          itemRes.json(),
        ]);

        setMetadata(collectionData);
        setItemMetadata(itemData);

        const [bgValid, iconValid, nftValid] = await Promise.all([
          checkIpfsUrl(collectionData?.cover_image),
          checkIpfsUrl(collectionData?.image),
          checkIpfsUrl(itemData?.image),
        ]);

        setLoading({
          background: !bgValid,
          image: !iconValid,
          nftImage: !nftValid,
          loaded: true,
        });
      } catch (e) {
        console.error("Metadata fetch error:", e);
      }
    };

    fetchData();
  }, [collectionUrl, itemUrl]);

  return { metadata, itemMetadata, loading };
}
